import React, { useState, useRef } from 'react';
import {
  IconButton, Popover, Box, Typography, FormControlLabel, Checkbox,
  Button, Divider, Tooltip, ToggleButtonGroup, ToggleButton
} from '@mui/material';
import { Settings as SettingsIcon, DragIndicator as DragIcon } from '@mui/icons-material';

const ProTablePreferences = ({ columns = [], preferences, pageSize, pageSizeOptions, onPageSizeChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const dragItem = useRef(null);
  const dragOver = useRef(null);

  // Use orderedColumns from preferences (respects user's drag order)
  const orderedCols = preferences.orderedColumns || columns;

  const handleDragStart = (index) => {
    dragItem.current = index;
  };

  const handleDragEnter = (index) => {
    dragOver.current = index;
  };

  const handleDragEnd = () => {
    if (dragItem.current !== null && dragOver.current !== null && dragItem.current !== dragOver.current) {
      preferences.moveColumn(dragItem.current, dragOver.current);
    }
    dragItem.current = null;
    dragOver.current = null;
  };

  return (
    <>
      <Tooltip title="Column settings">
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
        >
          <SettingsIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, minWidth: 240 }}>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, mb: 1 }}>
            Visible columns
          </Typography>
          <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', mb: 1 }}>
            Drag to reorder
          </Typography>
          {orderedCols.map((col, index) => (
            <Box
              key={col.key}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragEnter={() => handleDragEnter(index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => e.preventDefault()}
              sx={{
                display: 'flex',
                alignItems: 'center',
                py: 0.5,
                px: 0.5,
                borderRadius: 1,
                cursor: 'grab',
                '&:hover': { bgcolor: 'action.hover' },
                '&:active': { cursor: 'grabbing' },
              }}
            >
              <DragIcon sx={{ fontSize: 16, color: 'text.disabled', mr: 0.5, flexShrink: 0 }} />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={preferences.isVisible(col.key)}
                    onChange={() => preferences.toggleColumn(col.key)}
                  />
                }
                label={<Typography sx={{ fontSize: '0.8125rem' }}>{col.label}</Typography>}
                sx={{ m: 0, flex: 1 }}
              />
            </Box>
          ))}
          {onPageSizeChange && pageSizeOptions?.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, mb: 1 }}>
                Rows per page
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                fullWidth
                value={pageSize}
                onChange={(e, v) => { if (v) onPageSizeChange(v); }}
                sx={{
                  '& .MuiToggleButton-root': { fontSize: '0.75rem', py: 0.25 },
                  '& .MuiToggleButton-root.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': { bgcolor: 'primary.dark' },
                  },
                }}
              >
                {pageSizeOptions.map((n) => (
                  <ToggleButton key={n} value={n}>{n}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </>
          )}
          <Divider sx={{ my: 1 }} />
          <Button size="small" onClick={preferences.resetColumns}
            sx={{ fontSize: '0.75rem', textTransform: 'none' }}>
            Reset to default
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default ProTablePreferences;
